// =============================================================================
// AutomatedSuitePage — NDI Voice-Testing tab (embedded inside Test Lab)
// =============================================================================
// 4 sub-tabs: Overview (interactive diagram), A Specs, B Run & Live, C Results.
// Run (graded) → server-driven ndiSuiteRunSuite (the synthetic caller drives the
// agent, G0/G1 grade, RunResult persisted → Results). Exploratory → a manual
// browser call with live verdicts. Profiles come from the existing TEST_CALLERS.
// =============================================================================

import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { BrowserVoiceCall, type BrowserVoiceCallState } from '@adornis/voice-assistant/client/browser-call.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { of } from 'rxjs';
import ndiArchitectureUrl from '../assets/ndi-architecture.html';
import {
  testLabListCallers,
  ndiSuiteListSpecs,
  ndiSuiteSignOff,
  ndiSuiteRequestRun,
  ndiSuiteSubscribeVerdicts,
  ndiSuiteListRuns,
  ndiSuiteRunSuite,
  ndiSuiteCallTranscript,
  ndiSuitePersonaForProfile,
  ndiSuiteRunStatus,
  ndiSuiteLiveAudio,
} from '../../db/test-lab-resolvers.js';

type SubTabId = 'overview' | 'specs' | 'run' | 'results';
interface CallerUI {
  profileId: string;
  label: string;
  expectedFlow?: string;
}
interface SpecUI {
  specKey: string;
  version: number;
  name: string;
  description?: string;
  personaProfileId?: string;
  signedOffBy?: string;
}
interface VerdictUI {
  check: string;
  passed: boolean;
  kind: string;
  reason?: string;
  ts: number;
  final: boolean;
}
interface RunUI {
  runGroupID: string;
  createdAt: string;
  gatePassed?: boolean;
  passes: number;
  totalRuns: number;
  wilsonLower?: number;
  perRun?: string;
}
interface PerRunUI {
  runId: string;
  callID: string;
  verdict: string;
  g0?: string;
  g1?: string;
  reason?: string;
  verdicts?: Array<{ tier: string; source: string; passed: boolean; reason?: string }>;
}
interface TranscriptTurn {
  ts: number;
  role: 'caller' | 'agent' | 'tool';
  text: string;
}

const badge = (v: VerdictUI) =>
  html`<span class="badge ${v.passed ? 'badge-success' : v.kind === 'ordering-gate' ? 'badge-warning' : 'badge-error'}"
    >${v.passed ? 'PASS' : v.kind.toUpperCase()}</span
  >`;

export const AutomatedSuitePage = stateful(() => {
  const [subTab, setSubTab] = useState<SubTabId>('overview');
  const [callState, setCallState] = useState<BrowserVoiceCallState>('idle');
  const [call, setCall] = useState<BrowserVoiceCall | null>(null);
  const [activeSessionID, setActiveSessionID] = useState<string | null>(null);
  const [verdicts, setVerdicts] = useState<VerdictUI[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [runMsg, setRunMsg] = useState<string | null>(null);
  const [running, setRunning] = useState<boolean>(false);
  const [selectedLang, setSelectedLang] = useState<'de' | 'en'>('de');
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [exploratory, setExploratory] = useState<boolean>(false);
  const [runsReloadKey, setRunsReloadKey] = useState<number>(0);
  const [detailRun, setDetailRun] = useState<{ runGroupID: string; rows: PerRunUI[] } | null>(null);
  const [detailCallID, setDetailCallID] = useState<string | null>(null);
  // editable "playbook" (the caller robot's persona prompt), pre-filled per profile
  const [playbook, setPlaybook] = useState<string>('');
  const [playbookDirty, setPlaybookDirty] = useState<boolean>(false);
  // real-time live view of the in-flight graded run
  const [activeRunGroupID, setActiveRunGroupID] = useState<string | null>(null);
  const [liveDone, setLiveDone] = useState<boolean>(false);
  const [statusTick, setStatusTick] = useState<number>(0);
  // live "listen in" toggle — plays both robots' audio in real time via Web Audio
  const [listening, setListening] = useState<boolean>(false);
  // STABLE holders (useState init-once; functional-lit has no useRef). liveIdHolder
  // makes liveCallID sticky so the 2.5s status poll's transient null-blips can't flip
  // it and tear down the AudioContext mid-stream (the ~2.5s audio cut).
  const [liveIdHolder] = useState<{ v: string | null }>({ v: null });
  // one persistent AudioContext (created on a user gesture — Run click or the toggle —
  // and reused) so it's never torn down/recreated mid-call and starts UNLOCKED early.
  const [audioCtxHolder] = useState<{ ctx: AudioContext | null }>({ ctx: null });

  const transcriptQuery = useQuery(
    () => (detailCallID ? ndiSuiteCallTranscript(detailCallID)() : of(null)),
    [detailCallID],
  );
  const transcript: { turns: TranscriptTurn[]; recordingUrl: string | null; recordingState?: string } = (() => {
    try {
      const r = transcriptQuery.value as any;
      return typeof r === 'string' && r ? JSON.parse(r) : { turns: [], recordingUrl: null };
    } catch {
      return { turns: [], recordingUrl: null };
    }
  })();

  const callersQuery = useQuery(() => testLabListCallers()(), []);
  const specsQuery = useQuery(() => ndiSuiteListSpecs()(), []);
  const runsQuery = useQuery(() => ndiSuiteListRuns(20)(), [runsReloadKey, callState]);
  const verdictSub = useQuery(
    () => (activeSessionID ? ndiSuiteSubscribeVerdicts(activeSessionID)() : of(null)),
    [activeSessionID],
  );

  const callers: CallerUI[] = (() => {
    try {
      const r = callersQuery.value as any;
      return typeof r === 'string' && r ? JSON.parse(r) : [];
    } catch {
      return [];
    }
  })();
  const specs: SpecUI[] = (() => {
    try {
      const r = specsQuery.value as any;
      return typeof r === 'string' && r ? JSON.parse(r) : [];
    } catch {
      return [];
    }
  })();
  const runs: RunUI[] = (() => {
    try {
      const r = runsQuery.value as any;
      return typeof r === 'string' && r ? JSON.parse(r) : [];
    } catch {
      return [];
    }
  })();

  useEffect(() => {
    if (verdictSub.value && typeof verdictSub.value === 'string') {
      try {
        const v = JSON.parse(verdictSub.value as string) as VerdictUI;
        const last = verdicts[verdicts.length - 1];
        if (!last || last.ts !== v.ts) setVerdicts([...verdicts, v]);
      } catch {}
    }
  }, [verdictSub.value]);

  // ── editable playbook: load the default persona for the selected profile/lang ──
  const personaQuery = useQuery(
    () => (selectedProfileId && !exploratory ? ndiSuitePersonaForProfile(selectedProfileId, selectedLang)() : of(null)),
    [selectedProfileId, selectedLang, exploratory],
  );
  useEffect(() => {
    // (re)fill the textarea with the auto-built playbook whenever the profile/lang
    // changes — but never clobber edits the user has made (playbookDirty).
    if (!playbookDirty && typeof personaQuery.value === 'string' && personaQuery.value.length > 0) {
      setPlaybook(personaQuery.value as string);
    }
  }, [personaQuery.value, playbookDirty]);

  // ── real-time: poll the live status of the active graded run (~2.5s) ──
  // Gate only on activeRunGroupID (not liveDone): polling halts naturally because
  // statusTick stops incrementing once done, while the last value (with callIDs)
  // is retained so the finished conversation keeps showing.
  const statusQuery = useQuery(
    () => (activeRunGroupID ? ndiSuiteRunStatus(activeRunGroupID)() : of(null)),
    [activeRunGroupID, statusTick],
  );
  const liveStatus: { activeCallID: string | null; callIDs: string[]; done: boolean } = (() => {
    try {
      const r = statusQuery.value as any;
      return typeof r === 'string' && r ? JSON.parse(r) : { activeCallID: null, callIDs: [], done: false };
    } catch {
      return { activeCallID: null, callIDs: [], done: false };
    }
  })();
  // sticky: keep the last non-null call id (a null-blip on a poll must NOT flip it)
  const _liveId = liveStatus.activeCallID ?? liveStatus.callIDs[liveStatus.callIDs.length - 1] ?? null;
  if (_liveId) liveIdHolder.v = _liveId;
  const liveCallID = liveIdHolder.v;

  // a NEW run starts → clear the sticky id so it doesn't point at the previous call
  useEffect(() => {
    liveIdHolder.v = null;
  }, [activeRunGroupID]);

  // keep ticking while a run is live; flip liveDone (→ refresh Results) when the server reports done
  useEffect(() => {
    if (!activeRunGroupID || liveDone) return;
    if (liveStatus.done) {
      setLiveDone(true);
      setRunsReloadKey(Date.now());
      return;
    }
    const h = setTimeout(() => setStatusTick(statusTick + 1), 2500);
    return () => clearTimeout(h);
  }, [activeRunGroupID, statusTick, liveDone, liveStatus.done]);

  // ── LIVE LISTEN-IN: stream both legs' PCM (base64) and play via Web Audio ──────
  // The toggle button provides the user gesture AudioContext needs. We subscribe
  // directly (not useQuery) so EVERY frame is played, and schedule each buffer on a
  // running playhead so the conversation plays back gap-free. Cleanup tears down the
  // subscription + AudioContext. Client-only — cannot affect the server or /call.
  useEffect(() => {
    if (!listening || !liveCallID || liveDone) return;
    const AC: typeof AudioContext | undefined = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    if (!audioCtxHolder.ctx) audioCtxHolder.ctx = new AC(); // reuse the persistent ctx
    const ctx = audioCtxHolder.ctx;
    void ctx.resume();
    // Jitter buffer: keep a ~200ms lead so subscription latency / bursty TTS doesn't
    // underrun into gaps. If the playhead drifts behind real time (or too far ahead),
    // re-seed it ONCE with a fresh lead instead of clamping into repeated mid-stream gaps.
    const LEAD = 0.2;
    let playhead = ctx.currentTime + LEAD;
    const obs: any = ndiSuiteLiveAudio(liveCallID)();
    const sub = obs.subscribe({
      next: (raw: any) => {
        if (typeof raw !== 'string' || !raw) return;
        let pcm = '';
        try {
          const f = JSON.parse(raw);
          pcm = f && typeof f.pcm === 'string' ? f.pcm : '';
        } catch {
          return;
        }
        if (!pcm) return;
        let bin = '';
        try {
          bin = atob(pcm);
        } catch {
          return;
        }
        const n = bin.length >> 1;
        if (n <= 0) return;
        const audioBuf = ctx.createBuffer(1, n, 16000);
        const ch = audioBuf.getChannelData(0);
        for (let i = 0; i < n; i++) {
          let s = (bin.charCodeAt(2 * i + 1) << 8) | bin.charCodeAt(2 * i);
          if (s >= 0x8000) s -= 0x10000;
          ch[i] = s / 32768;
        }
        const node = ctx.createBufferSource();
        node.buffer = audioBuf;
        node.connect(ctx.destination);
        const now = ctx.currentTime;
        if (playhead < now + 0.02) playhead = now + LEAD; // re-seed only on underrun (behind real time)
        node.start(playhead);
        playhead += audioBuf.duration;
      },
      error: () => {
        /* stream ended / errored — cleanup handles teardown */
      },
    });
    return () => {
      try {
        sub.unsubscribe();
      } catch {}
      // do NOT close the shared AudioContext here — it is reused across the call and
      // closed only on unmount (below). Closing it here was the per-tick audio cut.
    };
  }, [listening, liveCallID, liveDone]);

  // auto-stop listening when the call finishes (the recording player takes over)
  useEffect(() => {
    if (liveDone && listening) setListening(false);
  }, [liveDone]);
  // close the persistent AudioContext once, on unmount
  useEffect(
    () => () => {
      try {
        void audioCtxHolder.ctx?.close();
      } catch {}
    },
    [],
  );

  // live transcript of the call currently in flight (re-fetched on every tick;
  // holds its last value after the run finishes since statusTick freezes)
  const liveTranscriptQuery = useQuery(
    () => (liveCallID ? ndiSuiteCallTranscript(liveCallID)() : of(null)),
    [liveCallID, statusTick],
  );
  const liveTranscript: { turns: TranscriptTurn[]; recordingUrl: string | null } = (() => {
    try {
      const r = liveTranscriptQuery.value as any;
      return typeof r === 'string' && r ? JSON.parse(r) : { turns: [], recordingUrl: null };
    } catch {
      return { turns: [], recordingUrl: null };
    }
  })();

  // ── graded run: server drives the synthetic caller, grades, persists RunResult ──
  const startGradedRun = async () => {
    if (!selectedProfileId) {
      setErrorMsg('Select a caller profile first (or use Exploratory).');
      return;
    }
    setErrorMsg(null);
    setRunMsg(null);
    setRunning(true);
    // AUTO-ARM live audio: create + resume the AudioContext NOW (inside the click's
    // user-gesture window) and turn listening on, so the player is unlocked and
    // subscribed as soon as the call id appears — capturing the agent's opening.
    try {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AC && !audioCtxHolder.ctx) audioCtxHolder.ctx = new AC();
      void audioCtxHolder.ctx?.resume();
      setListening(true);
    } catch {}
    // reset live view
    setActiveRunGroupID(null);
    setLiveDone(false);
    setStatusTick(0);
    try {
      // No script → the server builds an LLM-persona caller. A non-empty playbook
      // overrides that persona (the user's edited "playbook" for the robot).
      const caseObj: any = { profileId: selectedProfileId, lang: selectedLang, label: selectedProfileId };
      if (playbook.trim().length > 0) caseObj.persona = playbook;
      const casesJSON = JSON.stringify([caseObj]);
      const res = (await ndiSuiteRunSuite(casesJSON, 'smoke', '')()) as unknown as string;
      let rg = '';
      try {
        rg = JSON.parse(res)?.runGroupID ?? '';
      } catch {}
      setActiveRunGroupID(rg || null);
      setLiveDone(false);
      setRunMsg('✓ Run started — listening live below. The graded result lands in Results in ~60-90s.');
      // stay on Run & Live so the user hears/sees the conversation in real time
      setRunsReloadKey(Date.now());
      setTimeout(() => setRunsReloadKey(Date.now()), 45000);
      setTimeout(() => setRunsReloadKey(Date.now()), 75000);
      setTimeout(() => setRunsReloadKey(Date.now()), 105000);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  // ── exploratory: manual browser call + live verdicts ──
  const startExploratory = async () => {
    setErrorMsg(null);
    setVerdicts([]);
    try {
      const token = selectedProfileId
        ? ((await ndiSuiteRequestRun(selectedLang, selectedProfileId, '')()) as unknown as string)
        : undefined;
      if (token && !token.startsWith('tlt-')) throw new Error(`bad token: ${token}`);
      const c = new BrowserVoiceCall({
        url: '/api/browser-call/ws',
        meta: token ? { testToken: token } : {},
        onStateChange: s => setCallState(s),
      });
      setCall(c);
      setActiveSessionID(await c.start());
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
      setCallState('error');
    }
  };
  const stopExploratory = async () => {
    if (call && callState === 'connected') await call.stop();
    setCall(null);
  };

  const signOff = async (s: SpecUI) => {
    try {
      await ndiSuiteSignOff(s.specKey, s.version, '', '')();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  };

  const subTabBar = html`<div role="tablist" class="tabs tabs-boxed mb-6 inline-flex w-fit">
    ${(
      [
        ['overview', 'Overview'],
        ['specs', 'A · Specs'],
        ['run', 'B · Run & Live'],
        ['results', 'C · Results'],
      ] as Array<[SubTabId, string]>
    ).map(
      ([id, label]) =>
        html`<button role="tab" class="tab ${subTab === id ? 'tab-active' : ''}" @click=${() => setSubTab(id)}>
          ${label}
        </button>`,
    )}
  </div>`;

  const overviewTab = html`<div class="card bg-base-100 border border-base-300 shadow-sm">
    <div class="card-body p-4">
      <iframe
        src="${ndiArchitectureUrl}"
        title="NDI Voice-Testing Architecture"
        class="w-full rounded-lg border border-base-300"
        style="height:72vh;background:#fff"
        sandbox="allow-scripts"
      ></iframe>
    </div>
  </div>`;

  const specsTab = html`<div class="card bg-base-100 border border-base-300 shadow-sm">
    <div class="card-body p-5">
      <h3 class="font-medium mb-3">Specs & Sign-off</h3>
      ${specs.length === 0
        ? html`<p class="text-sm opacity-60 italic">No specs yet (spec authoring UI coming next).</p>`
        : html`<ul class="space-y-2">
            ${specs.map(
              s =>
                html`<li class="flex items-center gap-3 rounded-lg border border-base-300 bg-base-200/40 px-3 py-2">
                  <div class="flex-1">
                    <div class="font-medium text-sm">
                      ${s.name} <span class="opacity-50 text-xs">v${s.version}</span>
                    </div>
                    <div class="text-xs opacity-70">${s.description ?? ''}</div>
                  </div>
                  ${s.signedOffBy
                    ? html`<span class="badge badge-success badge-sm">signed</span>`
                    : html`<button class="btn btn-primary btn-xs" @click=${() => signOff(s)}>Sign off</button>`}
                </li>`,
            )}
          </ul>`}
    </div>
  </div>`;

  const runTab = html`<div class="flex flex-col items-center gap-3 mb-6">
    <div class="flex items-center gap-3 flex-wrap justify-center">
      <select
        class="select select-bordered select-sm"
        .value=${selectedProfileId ?? ''}
        @change=${(e: Event) => setSelectedProfileId((e.target as HTMLSelectElement).value || null)}
      >
        <option value="">— select caller profile —</option>
        ${callers.map(
          c =>
            html`<option value=${c.profileId} ?selected=${selectedProfileId === c.profileId}>
              ${c.label} (${c.expectedFlow ?? ''})
            </option>`,
        )}
      </select>
      <select
        class="select select-bordered select-sm"
        .value=${selectedLang}
        @change=${(e: Event) => setSelectedLang((e.target as HTMLSelectElement).value as 'de' | 'en')}
      >
        <option value="de" ?selected=${selectedLang === 'de'}>Deutsch</option>
        <option value="en" ?selected=${selectedLang === 'en'}>English</option>
      </select>
      <label class="label cursor-pointer gap-2 text-sm"
        ><input
          type="checkbox"
          class="checkbox checkbox-sm"
          .checked=${exploratory}
          @change=${(e: Event) => setExploratory((e.target as HTMLInputElement).checked)}
        />
        Exploratory (manual, live)</label
      >
    </div>
    ${!exploratory && selectedProfileId
      ? html`<div class="card bg-base-200 border border-base-300 w-full max-w-3xl">
          <div class="card-body p-4 gap-2">
            <div class="flex items-center justify-between">
              <h3 class="font-medium text-sm">
                Playbook · what the robot caller says <span class="opacity-50 font-normal">(edit before running)</span>
              </h3>
              <button
                class="btn btn-ghost btn-xs"
                ?disabled=${!playbookDirty}
                @click=${() => {
                  setPlaybookDirty(false);
                  if (typeof personaQuery.value === 'string') setPlaybook(personaQuery.value as string);
                }}
              >
                ↺ reset to default
              </button>
            </div>
            <textarea
              class="textarea textarea-bordered w-full font-mono text-xs leading-snug"
              rows="9"
              .value=${playbook}
              @input=${(e: Event) => {
                setPlaybook((e.target as HTMLTextAreaElement).value);
                setPlaybookDirty(true);
              }}
              placeholder=${personaQuery.loading
                ? 'loading default playbook…'
                : 'persona / instructions for the synthetic caller…'}
            ></textarea>
            <p class="text-xs opacity-60">
              This is the prompt that drives the robot that <em>answers</em> (never the agent under test). The robot
              waits for the agent to finish, answers only what is asked, and gives one piece of info per
              turn.${playbookDirty ? html` <span class="text-warning font-medium">· edited</span>` : nothing}
            </p>
          </div>
        </div>`
      : nothing}
    ${exploratory
      ? html`<button
          class="btn shadow-lg ${callState === 'connected' ? 'btn-error' : 'btn-primary'}"
          ?disabled=${callState === 'connecting'}
          @click=${() => (callState === 'connected' ? stopExploratory() : startExploratory())}
        >
          ${callState === 'connected' ? 'Stop' : 'Start manual call'}
        </button>`
      : html`<button
          class="btn btn-primary shadow-lg"
          ?disabled=${running || !selectedProfileId}
          @click=${() => startGradedRun()}
        >
          ${running ? html`<span class="loading loading-spinner loading-xs"></span> Running…` : 'Run automated (robot)'}
        </button>`}
    <p class="text-sm text-base-content/60">
      ${exploratory
        ? html`status: <span class="font-medium">${callState}</span>${activeSessionID
              ? html` · <span class="font-mono text-xs">${activeSessionID}</span>`
              : nothing}`
        : html`The robot calls the agent, grades it, and writes a result (~30-60s).`}
    </p>
    ${runMsg ? html`<div class="alert alert-success text-sm">${runMsg}</div>` : nothing}
    ${errorMsg ? html`<div class="alert alert-error text-sm"><strong>Error:</strong> ${errorMsg}</div>` : nothing}
    ${exploratory && activeSessionID
      ? html`<div class="card bg-base-200 w-full">
          <div class="card-body p-4">
            <h3 class="font-medium mb-2">Live verdicts</h3>
            ${verdicts.length === 0
              ? html`<p class="text-sm italic opacity-60">Waiting for first tool execution…</p>`
              : html`<ul class="space-y-2">
                  ${verdicts.map(
                    v =>
                      html`<li class="flex items-start gap-2 p-2 bg-base-100 rounded">
                        ${badge(v)}
                        <div class="flex-1">
                          <span class="font-mono text-sm">${v.check}</span>${v.reason
                            ? html`<span class="text-xs opacity-70 ml-2">${v.reason}</span>`
                            : nothing}
                        </div>
                      </li>`,
                  )}
                </ul>`}
          </div>
        </div>`
      : nothing}
    ${!exploratory && activeRunGroupID
      ? html`<div class="card bg-base-200 border border-base-300 w-full max-w-3xl">
          <div class="card-body p-4">
            <div class="flex items-center justify-between mb-2">
              <h3 class="font-medium">
                ${liveDone
                  ? 'Conversation (finished)'
                  : html`<span class="loading loading-dots loading-sm align-middle"></span> Live conversation`}
              </h3>
              <div class="flex items-center gap-2">
                ${liveCallID && !liveDone
                  ? html`<button
                      class="btn btn-xs ${listening ? 'btn-success' : 'btn-outline'}"
                      title="Hear both robots in real time"
                      @click=${() => setListening(!listening)}
                    >
                      ${listening ? '🔊 listening…' : '🔊 listen live'}
                    </button>`
                  : nothing}
                ${liveDone
                  ? html`<button
                      class="btn btn-primary btn-xs"
                      @click=${() => {
                        setSubTab('results');
                        setRunsReloadKey(Date.now());
                      }}
                    >
                      View graded result →
                    </button>`
                  : html`<span class="text-xs opacity-60"
                      >${liveCallID
                        ? html`call <span class="font-mono">${liveCallID.slice(0, 8)}</span>`
                        : 'connecting…'}</span
                    >`}
              </div>
            </div>
            ${liveTranscript.turns.length === 0
              ? html`<p class="text-sm italic opacity-60">
                  ${liveCallID ? 'waiting for the first words…' : 'the robot is dialing the agent…'}
                </p>`
              : html`<div class="space-y-1 font-mono text-xs max-h-80 overflow-y-auto">
                  ${repeat(
                    liveTranscript.turns,
                    (t, i) => `${t.ts}-${i}`,
                    t =>
                      html`<div class="flex gap-2">
                        <span class="opacity-40 shrink-0">${new Date(t.ts).toLocaleTimeString('de-DE')}</span>
                        <span
                          class="shrink-0 font-medium ${t.role === 'agent'
                            ? 'text-primary'
                            : t.role === 'caller'
                              ? 'text-success'
                              : 'opacity-50'}"
                          >${t.role === 'agent' ? 'AGENT' : t.role === 'caller' ? 'ROBOT' : 'TOOL'}:</span
                        >
                        <span class="${t.role === 'tool' ? 'opacity-50' : ''}">${t.text}</span>
                      </div>`,
                  )}
                </div>`}
            ${liveDone && liveTranscript.recordingUrl
              ? html`<audio
                  controls
                  preload="none"
                  src="${liveTranscript.recordingUrl}"
                  class="w-full mt-3"
                  style="height:36px"
                ></audio>`
              : nothing}
          </div>
        </div>`
      : nothing}
  </div>`;

  const openDetail = (r: RunUI) => {
    let rows: PerRunUI[] = [];
    try {
      rows = r.perRun ? JSON.parse(r.perRun) : [];
    } catch {}
    setDetailRun({ runGroupID: r.runGroupID, rows });
    setDetailCallID(rows[0]?.callID ?? null);
  };
  const resultsTab = html`<div class="card bg-base-100 border border-base-300 shadow-sm">
    <div class="card-body p-5">
      <h3 class="font-medium mb-3">Results & Report</h3>
      ${runs.length === 0
        ? html`<p class="text-sm opacity-60 italic">No runs yet. Go to Run & Live → pick a profile → Run automated.</p>`
        : html`<div class="overflow-x-auto">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Result</th>
                  <th>Pass-rate</th>
                  <th>Run</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                ${runs.map(r => {
                  const p = r.passes ?? 0;
                  const tot = r.totalRuns ?? 0;
                  const v = tot > 0 && p === tot ? 'PASS' : p > 0 ? 'PARTIAL' : 'FAIL';
                  const cls = v === 'PASS' ? 'badge-success' : v === 'PARTIAL' ? 'badge-warning' : 'badge-error';
                  return html`<tr class="${detailRun?.runGroupID === r.runGroupID ? 'bg-base-200' : ''}">
                    <td class="font-mono text-xs">${new Date(r.createdAt).toLocaleString('de-DE')}</td>
                    <td><span class="badge ${cls}">${v}</span></td>
                    <td class="font-mono">
                      ${p}/${tot}${r.wilsonLower != null ? ` (≥${Math.round(r.wilsonLower * 100)}%)` : ''}
                    </td>
                    <td class="font-mono text-xs opacity-60">${r.runGroupID}</td>
                    <td><button class="btn btn-ghost btn-xs" @click=${() => openDetail(r)}>🔍 details</button></td>
                  </tr>`;
                })}
              </tbody>
            </table>
          </div>`}
      ${detailRun
        ? html`<div class="mt-5 border-t border-base-300 pt-4">
            <div class="flex items-center justify-between mb-2">
              <h4 class="font-medium">Run detail</h4>
              <button
                class="btn btn-ghost btn-xs"
                @click=${() => {
                  setDetailRun(null);
                  setDetailCallID(null);
                }}
              >
                ✕ close
              </button>
            </div>
            <ul class="space-y-1 mb-3">
              ${detailRun.rows.map(
                row =>
                  html`<li class="text-sm flex flex-wrap items-center gap-2">
                    <span
                      class="badge badge-sm ${row.verdict === 'PASS'
                        ? 'badge-success'
                        : row.verdict === 'FAIL'
                          ? 'badge-error'
                          : 'badge-warning'}"
                      >${row.verdict}</span
                    >
                    <span class="opacity-70 text-xs">G0 ${row.g0 ?? '-'} · G1 ${row.g1 ?? '-'}</span>
                    ${(row.verdicts ?? [])
                      .filter(x => !x.passed && x.reason)
                      .map(x => html`<span class="text-xs opacity-60">· ${x.tier}: ${x.reason}</span>`)}
                    <button
                      class="btn btn-ghost btn-xs ml-auto ${detailCallID === row.callID ? 'btn-active' : ''}"
                      @click=${() => setDetailCallID(row.callID)}
                    >
                      conversation
                    </button>
                  </li>`,
              )}
            </ul>
            ${detailCallID
              ? html`<div class="bg-base-200 rounded-lg p-3">
                  ${transcript.recordingUrl
                    ? html`<audio
                        controls
                        preload="none"
                        src="${transcript.recordingUrl}"
                        class="w-full mb-3"
                        style="height:36px"
                      ></audio>`
                    : html`<p class="text-xs opacity-50 mb-2">
                        ${transcript.recordingState === 'expired'
                          ? 'Recording deleted after 21 days. Transcript retained.'
                          : 'no recording'}
                      </p>`}
                  ${transcriptQuery.loading
                    ? html`<p class="text-sm italic opacity-60">loading conversation…</p>`
                    : transcript.turns.length === 0
                      ? html`<p class="text-sm italic opacity-60">no transcript</p>`
                      : html`<div class="space-y-1 font-mono text-xs">
                          ${transcript.turns.map(
                            t =>
                              html`<div class="flex gap-2">
                                <span class="opacity-40 shrink-0">${new Date(t.ts).toLocaleTimeString('de-DE')}</span>
                                <span
                                  class="shrink-0 font-medium ${t.role === 'agent'
                                    ? 'text-primary'
                                    : t.role === 'caller'
                                      ? 'text-success'
                                      : 'opacity-50'}"
                                  >${t.role === 'agent' ? 'AGENT' : t.role === 'caller' ? 'ROBOT' : 'TOOL'}:</span
                                >
                                <span class="${t.role === 'tool' ? 'opacity-50' : ''}">${t.text}</span>
                              </div>`,
                          )}
                        </div>`}
                </div>`
              : nothing}
          </div>`
        : nothing}
    </div>
  </div>`;

  return html`<div class="flex flex-col">
    ${subTabBar} ${subTab === 'overview' ? overviewTab : nothing} ${subTab === 'specs' ? specsTab : nothing}
    ${subTab === 'run' ? runTab : nothing} ${subTab === 'results' ? resultsTab : nothing}
  </div>`;
});
